import React, { useState, useEffect, useRef } from "react";
import { GoChevronDown } from "react-icons/go";
import classNames from "classnames";
import { Option } from "../../types";

interface PaginationDropdownProps {
  placeholder?: string;
  options: Option[];
  value?: Option;
  onChange: (selectedOption: Option) => void;
  className?: string;
  disabled?: boolean;
}

const PaginationDropdown: React.FC<PaginationDropdownProps> = ({
  placeholder,
  options,
  value,
  onChange,
  className,
  disabled,
}) => {
  // hooks
  const [isOpen, setIsOpen] = useState(false);
  const divEl = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!divEl.current) return;
      if (!divEl.current.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener("click", handler, false);

    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  // classes
  const containerClasses = classNames(className, "relative inline-block text-left");

  // event handlers
  const toggleDropdown = () => {
    if (!disabled) setIsOpen(!isOpen);
  };

  const handleOptionClick = (selectedOption: Option) => {
    onChange(selectedOption);
    setIsOpen(false);
  };

  return (
    <div ref={divEl} className={containerClasses}>
      <button
        type="button"
        className="inline-flex justify-between items-center w-full rounded-md border border-gray-300 shadow-sm px-3 py-1 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={toggleDropdown}
        disabled={disabled}
      >
        {value?.label || placeholder || "Select..."}
        <GoChevronDown className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-full min-w-[60px] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="py-1">
            {options.map((option) => (
              <div
                key={option.value}
                className={classNames(
                  "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 cursor-pointer",
                  { "bg-gray-100 text-gray-900 font-semibold": value?.value === option.value }
                )}
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaginationDropdown;
